import { EditorState, Modifier } from 'draft-js'
import {
  getEntityRange,
  getSelectionEntity,
  getSelectionText,
} from 'draftjs-utils'
import React, { useCallback, useEffect } from 'react'
import type { LinkProps } from 'types/content-editor'
import { removeLink } from '@disco/content-editor/editor-utils'
import { fixLink, validateLink } from '@disco/utils/links'
import TextField from 'components/ui/text-field'

const getLinkProps = (editorState: EditorState): LinkProps => {
  const contentState = editorState.getCurrentContent()
  const entityKey = getSelectionEntity(editorState)

  if (entityKey) {
    const range = getEntityRange(editorState, entityKey)
    const { url } = contentState.getEntity(entityKey).getData()
    return {
      caption: range.text,
      url,
    }
  } else {
    return {
      caption: getSelectionText(editorState),
      url: '',
    }
  }
}

type Props = {
  editorState: EditorState
  onChange: (state: EditorState) => void
}

const LinkSettings = ({
  editorState,
  onChange,
}: Props): React.ReactElement<'form'> => {
  const { caption, url } = getLinkProps(editorState)
  useEffect(() => {
    return () => {
      onChange((editorState: EditorState) => {
        const { url } = getLinkProps(editorState)

        if (!validateLink(url)) {
          return removeLink(editorState)
        }

        return editorState
      })
    }
  }, [])
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target
      onChange((editorState: EditorState) => {
        let { caption, url } = getLinkProps(editorState)

        if (name === 'caption') {
          caption = value
        }

        if (name === 'url') {
          if (caption === url) {
            caption = fixLink(value)
          }

          url = fixLink(value)
        }

        const contentState = editorState.getCurrentContent()
        const entityKey = getSelectionEntity(editorState)
        let content
        let selection = editorState.getSelection()

        if (entityKey) {
          const range = getEntityRange(editorState, entityKey)
          selection = selection.merge({
            anchorOffset: range.start,
            focusOffset: range.end,
          })
          content = Modifier.replaceText(
            contentState,
            selection,
            caption,
            null,
            entityKey,
          )
          content = content.mergeEntityData(entityKey, {
            url,
          })
        } else {
          const newContent = contentState.createEntity('LINK', 'IMMUTABLE', {
            target: '_blank',
            url,
          })
          const entityKey = newContent.getLastCreatedEntityKey()
          content = Modifier.replaceText(
            contentState,
            selection,
            caption,
            null,
            entityKey,
          )
        }

        return EditorState.push(editorState, content, 'insert-characters')
      })
    },
    [onChange],
  )
  return (
    <form className="HTMLEditor-popover" autoComplete="off">
      <div className="HTMLEditor-popover-field">
        <TextField
          compact={false}
          placeholder="Add text"
          name="caption"
          value={caption}
          onChange={handleChange}
        />
      </div>
      <div className="HTMLEditor-popover-field">
        <TextField
          compact={false}
          placeholder="Add URL"
          name="url"
          value={url}
          onChange={handleChange}
        />
      </div>
    </form>
  )
}

export default LinkSettings
